import { config } from '../config';
import { ERROR_MESSAGES } from './constants';

/**
 * Ask the help-center agent a question.
 *
 * Sends a single POST to the Cloud Function and returns the structured
 * response: { answer, citations[], related_articles[], confidence }.
 *
 * Pass an AbortSignal to cancel in-flight requests (e.g. on a new submit)
 * and to enforce a client-side timeout.
 */
export async function askAgent(question, { signal } = {}) {
  let res;
  try {
    res = await fetch(config.apiUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ question }),
      signal,
    });
  } catch (err) {
    // AbortError / TimeoutError surface to the caller as-is so the UI can
    // distinguish a user-initiated cancel from an actual network failure.
    if (err?.name === 'AbortError' || err?.name === 'TimeoutError') throw err;
    throw new Error(ERROR_MESSAGES.NETWORK_ERROR);
  }

  // Defensive: a monkey-patched fetch / SW / polyfill could in theory resolve
  // with something that isn't a Response. Without this guard we would throw a
  // raw TypeError ("Cannot read properties of undefined (reading 'status')")
  // and surface it to the customer.
  if (!res || typeof res.status !== 'number') {
    throw new Error(ERROR_MESSAGES.AGENT_UNAVAILABLE);
  }

  // Per security review, the Cloud Function always returns HTTP 200 for
  // application-level outcomes; failures are signalled in the body as
  // `{ ok: false, errorCode, error }`. The status-code branches below are
  // kept as belt-and-suspenders fallbacks in case the CF is rolled back or
  // an upstream proxy injects an HTTP error.
  if (!res.ok) {
    if (res.status === 429) throw new Error(ERROR_MESSAGES.RATE_LIMITED);
    throw new Error(ERROR_MESSAGES.AGENT_UNAVAILABLE);
  }

  const data = await res.json().catch(() => null);

  if (data?.ok === false) {
    switch (data.errorCode) {
      case 'RATE_LIMITED':
        throw new Error(ERROR_MESSAGES.RATE_LIMITED);
      case 'INVALID_INPUT':
        throw new Error(data.error || ERROR_MESSAGES.GENERIC_ERROR);
      case 'AGENT_UNAVAILABLE':
      case 'METHOD_NOT_ALLOWED':
      default:
        throw new Error(ERROR_MESSAGES.AGENT_UNAVAILABLE);
    }
  }

  if (!data?.answer) {
    throw new Error(ERROR_MESSAGES.AGENT_UNAVAILABLE);
  }

  return data;
}

/**
 * Submit a thumbs-up/down feedback event for an answered question.
 *
 * Payload mirrors the Cloud Function's `/help-center-feedback` contract:
 *   - The four HMAC-signed fields (`question`, `answer`, `confidence`,
 *     `answerTimestamp`) must be byte-identical to what the CF returned
 *     with the answer — otherwise the CF rejects with 403 (signature
 *     mismatch).
 *   - `confidence` must be uppercased (`HIGH | MEDIUM | LOW`); the CF
 *     uppercases before signing, so the sender must match.
 *   - `answer_context[]` echoes back the citations/videos/related
 *     articles the user saw (built via `buildAnswerContext`). Not
 *     signed — the CF caps it at 30 entries as defense-in-depth.
 *   - `feedbackToken` is the 64-char hex HMAC the CF returned alongside
 *     the answer.
 *
 * Fire-and-forget on the client: errors (network, 403 tampered, 410
 * expired, 400 invalid) are logged at warn level and the promise
 * resolves with `{ ok: false, reason }` so callers can ignore the
 * result entirely. The thumbs UI optimistically locks the buttons on
 * click and shows "Thanks for your feedback!" regardless — the
 * analytics signal is best-effort.
 *
 * Pass an AbortSignal if you want unmount-cancel semantics.
 */
export async function submitFeedback(payload, { signal } = {}) {
  let res;
  try {
    res = await fetch(config.feedbackUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      signal,
    });
  } catch (err) {
    if (err?.name === 'AbortError' || err?.name === 'TimeoutError') {
      return { ok: false, reason: err.name };
    }
    console.warn('Feedback submit network error:', err?.message);
    return { ok: false, reason: 'network_error' };
  }

  if (!res || typeof res.status !== 'number') {
    console.warn('Feedback submit got malformed response');
    return { ok: false, reason: 'malformed_response' };
  }

  // 403 with no body = HMAC mismatch or non-Cloudflare bypass attempt.
  // Treat as a silent failure on the client; almost always means a stale
  // token / cache-rolled-over.
  if (res.status === 403) {
    console.warn('Feedback rejected: signature mismatch (token may be stale)');
    return { ok: false, reason: 'signature_mismatch' };
  }

  if (!res.ok) {
    console.warn('Feedback HTTP error:', res.status);
    return { ok: false, reason: `http_${res.status}` };
  }

  const data = await res.json().catch(() => null);

  // CF's contract: 200 + { ok: true } on success, 200 + { ok: false,
  // errorCode } for application-level errors (TOKEN_EXPIRED, INVALID_INPUT).
  if (data?.ok === true) {
    return { ok: true };
  }
  if (data?.ok === false) {
    console.warn('Feedback application error:', data.errorCode, data.error);
    return { ok: false, reason: data.errorCode || 'unknown' };
  }
  console.warn('Feedback unexpected response shape');
  return { ok: false, reason: 'unexpected_shape' };
}
